import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  serviceCenterRepairsApi,
  type SaveRemontDto,
} from '@/api/service-center-repairs';

/**
 * Get all remont orders + vendors list
 */
export function useRemontOrders() {
  return useQuery({
    queryKey: ['service-center-repairs', 'remont', 'active'],
    queryFn: () => serviceCenterRepairsApi.getAll(),
    staleTime: 60_000,
  });
}

/**
 * Get remont orders for a specific client.
 */
export function useRemontByClient(clientId: number | undefined, archive: boolean) {
  return useQuery({
    queryKey: ['service-center-repairs', 'remont', 'client', clientId, archive ? 1 : 0],
    queryFn: () => serviceCenterRepairsApi.getByClient(clientId!, archive),
    enabled: !!clientId,
    staleTime: 60_000,
  });
}

/**
 * Backward-compatible fallback for screens that only have a company name.
 */
export function useRemontByCompany(company: string, archive: boolean) {
  return useQuery({
    queryKey: ['service-center-repairs', 'remont', 'byCompany', company, archive ? 1 : 0],
    queryFn: () => serviceCenterRepairsApi.getByCompanyName(company, archive),
    enabled: !!company,
    staleTime: 60_000,
  });
}

/**
 * Save remont entry (create or update)
 */
export function useSaveRemont() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dto, photo }: { dto: SaveRemontDto; photo?: Blob }) =>
      serviceCenterRepairsApi.save(dto, photo),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['service-center-repairs', 'remont'] });
      toast.success(data.action === 'created' ? 'Заявка создана' : 'Заявка обновлена');
    },
    onError: () => {
      toast.error('Ошибка сохранения');
    },
  });
}
